<template>
  <div class="allbeds-page">

    <!-- Page header -->
    <div class="page-header">
      <button class="back-btn" onclick="window.history.back()"><i class="ft-arrow-left"></i></button>
      <div class="header-info">
        <span class="page-title">All Beds</span>
        <span class="page-subtitle"><i class="ft-calendar"></i> {{ date }}</span>
      </div>
    </div>

    <!-- Step 1: Floor / Ward selection -->
    <div class="section-pad">

      <!-- Floor tabs -->
      <div class="floor-tabs" v-if="merchant.use_floors == 'Y' && floors && floors.length > 0">
        <button
          class="floor-tab"
          :class="{ 'floor-tab--active': selectedFloorId === floor.floor_id }"
          v-for="floor in floors"
          :key="floor.floor_id"
          @click="selectFloor(floor)"
        >
          <i class="ft-layers"></i> {{ floor.floor_name }}
        </button>
      </div>

      <!-- Ward grid -->
      <div class="step-label" v-if="wards.length > 0 || (merchant.use_floors !== 'Y')">
        <div class="step-badge">1</div>
        <span>Select a ward</span>
      </div>

      <div class="ward-grid" v-if="wards.length > 0">
        <button
          class="ward-card"
          :class="{ 'ward-card--active': selectedWardId === w.ward_id }"
          v-for="w in wards"
          :key="w.ward_id"
          @click="selectWard(w)"
        >
          <div class="ward-card-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
          </div>
          <span class="ward-card-name">{{ w.ward_name }}</span>
          <div class="ward-card-check" v-if="selectedWardId === w.ward_id">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
        </button>
      </div>

      <div class="no-wards-hint" v-if="wards.length === 0 && merchant.use_floors == 'Y'">
        <i class="ft-info"></i> Select a floor to see wards.
      </div>
    </div>

    <!-- Step 2: search + count -->
    <div class="section-pad" v-if="beds.length > 0">
      <div class="ward-summary">
        <div class="step-label" style="margin-bottom:0">
          <div class="step-badge">2</div>
          <span>Manage patients</span>
        </div>
        <span class="bed-count-badge">{{ filteredBeds.length }} bed{{ filteredBeds.length !== 1 ? 's' : '' }}</span>
      </div>
      <div class="search-wrap">
        <i class="ft-search search-icon"></i>
        <input
          class="search-input"
          type="text"
          v-model="searchQuery"
          placeholder="Search by name or bed…"
        >
        <button class="search-clear" v-if="searchQuery" @click="searchQuery = ''">
          <i class="ft-x"></i>
        </button>
      </div>
    </div>

    <!-- No search results -->
    <div class="empty-state" v-if="beds.length > 0 && filteredBeds.length === 0">
      <i class="ft-search"></i>
      <p>No beds match <strong>"{{ searchQuery }}"</strong></p>
      <button class="clear-search-btn" @click="searchQuery = ''">Clear search</button>
    </div>

    <!-- Empty ward -->
    <div class="empty-state" v-if="beds.length === 0 && ward !== ''">
      <i class="ft-inbox"></i>
      <p>No beds found in this ward.</p>
    </div>

    <!-- Bed cards -->
    <div class="section-pad beds-section" v-if="filteredBeds.length > 0">
      <div class="beds-grid">
        <div class="patient-block" v-for="bed in filteredBeds" :key="bed.bed_id">
          <div class="bed-card" :class="{ 'bed-card--empty': !bed.patient_name }">

            <div class="bed-card-top">
              <div class="patient-avatar" :class="{ 'patient-avatar--empty': !bed.patient_name }">
                {{ bed.patient_name ? bed.patient_name.charAt(0).toUpperCase() : '—' }}
              </div>
              <div class="bed-info">
                <div class="bed-number">Bed {{ bed.bed_code }}</div>
                <div class="patient-name">{{ bed.patient_name || 'Empty bed' }}</div>
              </div>
              <button class="edit-bed-btn" @click="editBed(bed)" title="Manage bed">
                <i class="ft-more-vertical"></i>
              </button>
            </div>

            <div class="bed-card-meta" v-if="bed.patient_name">
              <span class="menu-pill" v-if="bed.menu_type_name"><i class="ft-bookmark"></i> {{ bed.menu_type_name }}</span>
              <span class="allergy-pill" v-if="bed.allergies && bed.allergies != ''">
                <i class="ft-alert-triangle"></i> {{ allergies(bed.allergies) }}
              </span>
            </div>

            <div class="important-info" v-if="bed.important_info != null && bed.important_info != ''">
              <i class="ft-alert-circle"></i> {{ bed.important_info }}
            </div>

            <div class="order-breakdown" v-if="bed.patient_name && bed.order_breakdown">
              <div class="breakdown-col">
                <div class="breakdown-day">Today</div>
                <div class="breakdown-dots">
                  <span class="meal-dot dot-b" v-if="bed.order_breakdown.today.breakfast">B</span>
                  <span class="meal-dot dot-l" v-if="bed.order_breakdown.today.lunch">L</span>
                  <span class="meal-dot dot-d" v-if="bed.order_breakdown.today.dinner">D</span>
                  <span class="meal-none" v-if="!bed.order_breakdown.today.breakfast && !bed.order_breakdown.today.lunch && !bed.order_breakdown.today.dinner">—</span>
                </div>
              </div>
              <div class="breakdown-divider"></div>
              <div class="breakdown-col">
                <div class="breakdown-day">Tomorrow</div>
                <div class="breakdown-dots">
                  <span class="meal-dot dot-b" v-if="bed.order_breakdown.tomorrow.breakfast">B</span>
                  <span class="meal-dot dot-l" v-if="bed.order_breakdown.tomorrow.lunch">L</span>
                  <span class="meal-dot dot-d" v-if="bed.order_breakdown.tomorrow.dinner">D</span>
                  <span class="meal-none" v-if="!bed.order_breakdown.tomorrow.breakfast && !bed.order_breakdown.tomorrow.lunch && !bed.order_breakdown.tomorrow.dinner">—</span>
                </div>
              </div>
            </div>

            <div class="place-order-cta" v-if="bed.patient_name" @click="generateLink(bed)">
              <i class="ft-plus-circle"></i> Place Order
            </div>

          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Patient options -->
    <div class="modal" tabindex="-1" role="dialog" id="editpateint-modal">
      <div class="modal-dialog" role="document">
        <div class="modal-content app-modal">
          <div class="app-modal-header">
            <span>Patient Options</span>
            <button type="button" class="modal-close-btn" data-dismiss="modal"><i class="ft-x"></i></button>
          </div>
          <div class="modal-body"><p class="modal-hint">Select a patient action:</p></div>
          <div class="app-modal-footer">
            <button type="button" class="modal-btn btn-discharge" v-on:click="discharge()" data-dismiss="modal"><i class="ft-log-out"></i> Discharge</button>
            <button type="button" class="modal-btn btn-transfer" v-on:click="transfer()" data-dismiss="modal"><i class="ft-repeat"></i> Transfer</button>
            <button type="button" class="modal-btn btn-edit" v-on:click="editMenu()" data-dismiss="modal"><i class="ft-edit"></i> Edit</button>
            <button type="button" class="modal-btn btn-order" v-on:click="generateLink(bed)" data-dismiss="modal"><i class="ft-plus-circle"></i> Place Order</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Discharge confirmation -->
    <div class="modal" tabindex="-1" role="dialog" id="discharge-modal">
      <div class="modal-dialog" role="document">
        <div class="modal-content app-modal">
          <div class="app-modal-header app-modal-header--danger">
            <span>Discharge Patient</span>
            <button type="button" class="modal-close-btn" data-dismiss="modal"><i class="ft-x"></i></button>
          </div>
          <div class="modal-body"><p class="modal-hint">Are you sure? The bed will become empty and any future orders will be cancelled.</p></div>
          <div class="app-modal-footer">
            <button type="button" class="modal-btn btn-discharge" v-on:click="dischargeNow()" data-dismiss="modal"><i class="ft-log-out"></i> Confirm Discharge</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Edit menu -->
    <div class="modal" tabindex="-1" role="dialog" id="edit-modal">
      <div class="modal-dialog" role="document">
        <div class="modal-content app-modal">
          <div class="app-modal-header">
            <span>Edit Patient</span>
            <button type="button" class="modal-close-btn" data-dismiss="modal"><i class="ft-x"></i></button>
          </div>
          <div class="modal-body">
            <div class="modal-field">
              <label class="select-label">Patient</label>
              <input type="text" class="modal-input" v-model="patient_name">
            </div>
            <div class="modal-field">
              <label class="select-label">Menu Type</label>
              <div class="select-wrap">
                <select class="menu_type" id="menu_type" v-on:change="setMenuType($event)">
                  <option v-for="menu in menu_types" v-if="menu.type_id == bed.menu_type_id" v-bind:value="menu.type_id" selected>{{menu.name}}</option>
                  <option v-if="bed.menu_type_id !='' || bed.menu_type_id == null || bed.menu_type_id == undefined" value="" selected>Select menu</option>
                  <option v-for="menu in menu_types" v-bind:value="menu.type_id">{{menu.name}}</option>
                </select>
                <i class="ft-chevron-down select-arrow"></i>
              </div>
            </div>
            <div class="modal-field">
              <label class="select-label">Special Info</label>
              <input type="text" class="modal-input" v-model="important_info">
            </div>
          </div>
          <div class="app-modal-footer">
            <button type="button" class="modal-btn btn-edit" v-on:click="saveBedMenu()" data-dismiss="modal"><i class="ft-check"></i> Save</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Add patient to empty bed -->
    <div class="modal" tabindex="-1" role="dialog" id="edit-bed-modal">
      <div class="modal-dialog" role="document">
        <div class="modal-content app-modal">
          <div class="app-modal-header">
            <span>Add Patient</span>
            <button type="button" class="modal-close-btn" data-dismiss="modal"><i class="ft-x"></i></button>
          </div>
          <div class="modal-body">
            <div class="modal-field">
              <label class="select-label">Patient Name *</label>
              <input type="text" class="modal-input" v-model="patient_name">
            </div>
            <div class="modal-field">
              <label class="select-label">Menu Type *</label>
              <div class="select-wrap">
                <select class="menu_type" id="menu_type" v-on:change="setMenuType($event)">
                  <option v-for="menu in menu_types" v-if="menu.type_id == bed.menu_type_id" v-bind:value="menu.type_id" selected>{{menu.name}}</option>
                  <option v-if="bed.menu_type_id !='' || bed.menu_type_id == null || bed.menu_type_id == undefined" value="" selected>Select menu</option>
                  <option v-for="menu in menu_types" v-bind:value="menu.type_id">{{menu.name}}</option>
                </select>
                <i class="ft-chevron-down select-arrow"></i>
              </div>
            </div>
            <div class="modal-field">
              <label class="select-label">Special Info</label>
              <input type="text" class="modal-input" v-model="important_info">
            </div>
          </div>
          <div class="app-modal-footer">
            <button type="button" class="modal-btn btn-edit" v-on:click="saveBedMenu(1)" data-dismiss="modal"><i class="ft-check"></i> Save</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Transfer -->
    <div class="modal" tabindex="-1" role="dialog" id="transfer-modal">
      <div class="modal-dialog" role="document">
        <div class="modal-content app-modal">
          <div class="app-modal-header">
            <span>Transfer Patient</span>
            <button type="button" class="modal-close-btn" data-dismiss="modal"><i class="ft-x"></i></button>
          </div>
          <div class="modal-body">
            <div class="modal-field">
              <label class="select-label">New Ward *</label>
              <div class="select-wrap">
                <select id="ward_id" v-on:change="setNewWardId($event)">
                  <option v-for="ward in wards" v-if="ward.ward_id == bed.ward_id" v-bind:value="getJson(ward)" selected>{{ward.ward_name}}</option>
                  <option v-for="ward in wards" v-bind:value="getJson(ward)">{{ward.ward_name}}</option>
                </select>
                <i class="ft-chevron-down select-arrow"></i>
              </div>
            </div>
            <div class="modal-field">
              <label class="select-label">New Bed *</label>
              <div class="select-wrap">
                <select id="new_bed" v-on:change="setNewBed($event)">
                  <option value="" selected>Select a bed</option>
                  <option v-for="bed in new_beds" v-if="(bed.patient_name == '' || bed.patient_name == null) && bed.bed_code != ''" v-bind:value="bed.bed_id">{{bed.bed_code}}</option>
                </select>
                <i class="ft-chevron-down select-arrow"></i>
              </div>
            </div>
          </div>
          <div class="app-modal-footer">
            <button type="button" class="modal-btn btn-edit" v-on:click="transferNow()" data-dismiss="modal"><i class="ft-check"></i> Save Transfer</button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import moment from 'moment'
import swal from 'sweetalert'
import Cart from '@/components/navigation/Cart.vue'

export default {
  name: 'ward',
  components: {
    Cart
  },
  data: function () {
    return {
      merchant: {},
      floors:[],
      date: moment().format('DD MMMM YYYY'),
      time: moment().format('h:mm'),
      wards: this.getWards(),
      beds: [],
      new_beds: [],
      periods: this.getPeriods(),
      period: '',
      cart: window.localStorage.getItem('cart') ? JSON.parse(window.localStorage.getItem('cart')) : [],
      ward:'',
      ward_id:'',
      new_ward_id:'',
      bed:"",
      menu_type:"",
      menu_types:[],
      new_bed_id:"",
      important_info:"",
      patient_name:"",
      searchQuery: '',
      selectedWardId: '',
      selectedFloorId: ''
    }
  },
  computed: {
    filteredBeds: function () {
      var q = this.searchQuery.toLowerCase().trim()
      if (!q) return this.beds
      return this.beds.filter(function (b) {
        return (b.patient_name || '').toLowerCase().includes(q) ||
               (b.bed_code || '').toLowerCase().includes(q)
      })
    }
  },
  mounted: function () {
    this.$store.commit('SET_LAYOUT', 'AppLayout')
    var user = JSON.parse(window.localStorage.getItem('user'))
    var that = this
   
    that.merchant = user.merchant
    that.floors = that.merchant.floors
    
    that.getMenuTypes()
    setInterval(function () {
      that.time = moment().format('h:mm')
    }, 60000)


    // Check if get a ward id from URL
    if (this.$route.query && this.$route.query.ward) {
      this.ward = this.$route.query.ward
      console.log(this.ward)
      that.ward_id = this.ward
      //setTimeout(function () {
        //that.getBeds()
     // }, 2000)
    }

    
  },
  methods: {
    selectFloor: function (floor) {
      this.selectedFloorId = floor.floor_id
      this.wards = floor.wards
      this.beds = []
      this.selectedWardId = ''
      this.ward = ''
    },
    selectWard: function (w) {
      this.selectedWardId = w.ward_id
      var event = { target: { value: JSON.stringify(w) } }
      this.wardChanged(event)
    },
    getJson: function(event){
      return JSON.stringify(event)
    },
    setNewBed: function(event){
      this.new_bed_id = event.target.value
       console.log(" this.new_bed_id", this.new_bed_id)
    },
    setMenuType: function(event){
      this.menu_type_id = event.target.value
       console.log(" this.menu_type_id", this.menu_type_id)
    },
    setNewWardId: function(event){
      var ward = JSON.parse(event.target.value)
      window.localStorage.setItem('ward',event.target.value)
     
      this.new_ward_id = ward.ward_id
      this.new_beds = ward.beds
      console.log(" this.new_ward_id", this.new_ward_id)
    },
    setJson: function(data){
      return JSON.stringify(data)
    },
    getWards: function () {
      var user = JSON.parse(window.localStorage.getItem('user'))
      console.log('use_floors',user.merchant.use_floors)
      if(user.merchant.use_floors == "Y"){
        return []
      }else{
        return user.merchant.wards
      }
      
    },
    floorChanged: function(event){
     this.wards = []
     this.wards = JSON.parse(event.target.value)
     console.log('wards',this.wards)
    },
    getPeriods: function () {
      var user = JSON.parse(window.localStorage.getItem('user'))
      var periods = []

      for (var key in user.merchant.periods) {
        periods.push({
          id: key,
          title: user.merchant.periods[key]
        })
      }

      return periods
    },
    showBed: function (bed) {
      /*
      var show = true
      var that = this

      if (this.period === '') {
        show = false
      } else if (bed.todays_orders) {
        bed.todays_orders.forEach(function (order) {
          if (order.period_id === that.period) {
            show = false
          }
        })
      }

      return show
      */
      return true
    },
    allergies: function (bedAllergies) {
      var allergies = ''

      bedAllergies.forEach(function (allergy) {
        allergies += allergy.name + ', '
      })

      return allergies.substring(0, allergies.length - 2)
    },
    wardChanged: function (event) {
      var that = this
      var ward = JSON.parse(event.target.value)
      var ward_id = ward.ward_id

       window.localStorage.setItem('ward', JSON.stringify(ward))
      var user = JSON.parse(window.localStorage.getItem('user'))
      var merchantId = user.merchant.id
      var has_menu_type = false
      if (ward_id !== '') {
        $.showLoading()
        $.ajax({
            beforeSend: function (xhr) {
            xhr.setRequestHeader('Authorization', window.localStorage.getItem('token'))
          },
          url: this.$config.apiUrl +'medicater/beds?merchant_id='+merchantId+'&ward_id='+ward_id+"&has_menu_type="+has_menu_type,
          type: 'get',
          success: function (data) {
            that.beds = data.data
            $.hideLoading()
            setTimeout(function () {
              $('.patient-block .card').matchHeight()
            }, 1000)
          },
          error: function (data) {
            console.log('data',data)
            $.hideLoading()
            if (window.localStorage.getItem(this.url)) {
              var offlineData = JSON.parse(window.localStorage.getItem(this.url))
              this.success(offlineData)
            } else {
              swal({
                text: data.responseJSON.message,
                icon: 'info',
                button: 'Ok'
              })
              .then(function (data) {
                //that.logout()
              })
            }
          }
        })
      }
    },
     logout: function () {
      localStorage.clear()
      this.$router.push('/login')
    },
    generateLink: function (bed) {
      window.localStorage.setItem('menu_type_id', bed.menu_type_id)
      window.localStorage.setItem('bed',JSON.stringify(bed))
      this.$router.push('/preorder/' + bed.ward_id + '/' + bed.bed_id)
    },
    saveBed: function (bed) {
      window.localStorage.setItem('bed', JSON.stringify(bed))
    },
    editBed: function(bed){
      console.log("bed",bed)
      this.important_info = ""
      this.bed = bed
      this.patient_name = bed.patient_name
      this.menu_type_id = this.bed.menu_type_id
      this.important_info = this.bed.important_info
      if(this.patient_name == '' || this.patient_name == null){
        this.transfer()
        return false
      }
      $('#editpateint-modal').modal('show');

    },
    discharge: function(){
      $('#discharge-modal').modal('show');
    },
    transfer: function(){
     
      
      var that = this

      if(that.bed.patient_name == "" || that.bed.patient_name == null){
        $('#edit-bed-modal').modal('show');
        if(that.bed.menu_type_id == ""){
            setTimeout(function(){
              $(".menu_type").val("")
            },500)
        }else{
          that.menu_type_id = that.bed.menu_type_id
        }
       
      }else{
        $('#transfer-modal').modal('show');
      }
      
      var user = JSON.parse(window.localStorage.getItem('user'))
      var merchantId = user.merchant.id
      var has_menu_type = false
      var get_beds = true
      var occupied = false
      
      that.new_ward_id = that.bed.ward_id

        $.showLoading()
        $.ajax({
            beforeSend: function (xhr) {
            xhr.setRequestHeader('Authorization', window.localStorage.getItem('token'))
          },
          url: this.$config.apiUrl +'medicater/wards?merchant_id='+merchantId+"&get_beds="+get_beds+"&occupied="+occupied,
          type: 'get',
          success: function (data) {
           
              that.wards = data.data
              var ward = ""
              for(var w in that.wards){
                if(that.bed.ward_id == that.wards[w].ward_id){
                  ward = that.wards[w]
                }
              }
              that.new_beds = ward.beds
            
            $.hideLoading()
            setTimeout(function () {
              $('.patient-block .card').matchHeight()
            }, 1000)
          },
          error: function (data) {
            console.log('data',data)
            $.hideLoading()
            if (window.localStorage.getItem(this.url)) {
              var offlineData = JSON.parse(window.localStorage.getItem(this.url))
              this.success(offlineData)
            } else {
              swal({
                text: data.responseJSON.message,
                icon: 'info',
                button: 'Ok'
              })
              .then(function (data) {
                //that.logout()
              })
            }
          }
        })
    },
    editMenu: function(){
      console.log("bed", this.bed)
      this.menu_type_id = this.bed.menu_type_id
      this.important_info = this.bed.important_info
      $('#edit-modal').modal('show');

       // setTimeout(function(){
       //    $(".menu_type").val("")
       //  },500)
    },
    dischargeNow: function(){
      //alertify.confirm("Are you sure you want to discharge this patient?","Confirm",function(){
         var user = JSON.parse(window.localStorage.getItem('user'))
        var that = this
      
        $.showLoading()
        $.ajax({
            beforeSend: function (xhr) {
            xhr.setRequestHeader('Authorization', window.localStorage.getItem('token'))
          },
          url: that.$config.apiUrl +'medicater/discharge?bed_id='+that.bed.bed_id,
          type: 'post',
          data:JSON.stringify({bed_id:that.bed.bed_id,staff_user_id:user.id}),
          success: function (data) {
            if(data.success){
               that.getBeds()
               swal({
                    text: data.message,
                    icon: 'success',
                    button: 'Ok'
                  })
                  .then(function (data) {
                    //that.logout()
                  })
             }else{
                swal({
                text: data.message,
                icon: 'info',
                button: 'Ok'
              })
              .then(function (data) {
                //that.logout()
              })
             }
           
          },
          error: function (data) {
            console.log('data',data)
            $.hideLoading()
            if (window.localStorage.getItem(that.url)) {
              var offlineData = JSON.parse(window.localStorage.getItem(that.url))
              that.success(offlineData)
            } else {
              swal({
                text: data.responseJSON.message,
                icon: 'info',
                button: 'Ok'
              })
              .then(function (data) {
                //that.logout()
              })
            }
          }
        })


     // })

    },
    transferNow: function(edit=""){
        var that = this
        var ward = JSON.parse(window.localStorage.getItem('ward'))
        var ward_id = ward.ward_id
        var user = JSON.parse(window.localStorage.getItem('user'))
        var merchantId = user.merchant.id
        var has_menu_type = user.merchant.menu_type
        var original_bed_id = that.bed.bed_id
        that.ward_id = ward_id
        if(edit){
          that.new_bed_id = that.bed.bed_id
        }
        if(that.patient_name== ""){
          swal({
            text: "Pateint name is required",
            icon: 'error',
            button: 'Ok'
          })
         
          return false
        }
        if(that.new_ward_id == ""){
        
           swal({
            text: "Please select a ward",
            icon: 'error',
            button: 'Ok'
          })
          return false
        }
        if(that.new_bed_id== ""){
       
           swal({
            text: "Please select a bed",
            icon: 'error',
            button: 'Ok'
          })
          return false
        }
        
        console.log("that.new_bed_id",that.new_bed_id)
        swal({
            text: "Are you sure you want to transfer this patient?",
            icon: 'warning',
            buttons: ['Cancel','Ok'],
            dangerMode: true,
          })
          .then(function (isConfirm) {
            if(!isConfirm){
              return false
            }
           
            var postData = {
              original_bed_id:original_bed_id,
              bed_id: that.new_bed_id,
              merchant_id:merchantId,
              ward_id:that.new_ward_id,
              staff_user_id:user.id,
              patient_name:that.patient_name,
              important_info:that.important_info
            }
            $.showLoading()
            $.ajax({
                beforeSend: function (xhr) {
                xhr.setRequestHeader('Authorization', window.localStorage.getItem('token'))
              },
              url: that.$config.apiUrl +'medicater/transfer?bed_id='+that.bed.bed_id,
              type: 'post',
              data:JSON.stringify(postData),
              success: function (data) {
                 if(data.success){
                   that.getBeds()
                   swal({
                    text: data.message,
                    icon: 'success',
                    button: 'Ok'
                  })
                  .then(function (data) {
                    //that.logout()
                  })
                 }else{
                    swal({
                    text: data.message,
                    icon: 'info',
                    button: 'Ok'
                  })
                  .then(function (data) {
                    //that.logout()
                  })
                 }
              },
              error: function (data) {
                console.log('data',data)
                $.hideLoading()
                if (window.localStorage.getItem(that.url)) {
                  var offlineData = JSON.parse(window.localStorage.getItem(that.url))
                  that.success(offlineData)
                } else {
                  swal({
                    text: data.responseJSON.message,
                    icon: 'info',
                    button: 'Ok'
                  })
                  .then(function (data) {
                    //that.logout()
                  })
                }
              }
            })


      })
    },
    saveBedMenu: function(edit=""){
        var that = this
        var ward = JSON.parse(window.localStorage.getItem('ward'))
        var ward_id = ward.ward_id
        var user = JSON.parse(window.localStorage.getItem('user'))
        var merchantId = user.merchant.id
        var has_menu_type = user.merchant.menu_type
        var original_bed_id = that.bed.bed_id

        $(".ajs-header").html("Confirm")
        var msg = "Are you sure you want to save this menu type?"
        if(edit){
          msg = "Are you sure you want to save this bed?"
        }
        if(that.menu_type_id == ""){
          that.menu_type_id = that.bed.menu_type_id
        }
        console.log("that.bed",that.bed)
        swal({
            text: msg,
            icon: 'warning',
            buttons: ['Cancel','Ok'],
            dangerMode: true,
          })
          .then(function (isConfirm) {
            if(!isConfirm){
              return false
            }
            var postData = {
              bed_id:that.bed.bed_id,
              merchant_id:merchantId,
              menu_type_id:that.menu_type_id,
              ward_id:ward_id,
              staff_user_id:user.id,
              patient_name:that.patient_name,
              important_info:that.important_info,
            }
            console.log("postData",postData)
            $.showLoading()
            $.ajax({
                beforeSend: function (xhr) {
                xhr.setRequestHeader('Authorization', window.localStorage.getItem('token'))
              },
              url: that.$config.apiUrl +'medicater/edit_patient?bed_id='+that.bed.bed_id,
              type: 'post',
              data:JSON.stringify(postData),
              success: function (data) {
                 if(data.success){
                    
                   that.getBeds()
                   swal({
                    text: data.message,
                    icon: 'success',
                    button: 'Ok'
                  })
                  .then(function (data) {
                    //that.logout()
                  })
                 }else{
                    swal({
                    text: data.message,
                    icon: 'info',
                    button: 'Ok'
                  })
                  .then(function (data) {
                    //that.logout()
                  })
                 }
              },
              error: function (data) {
                console.log('data',data)
                $.hideLoading()
                if (window.localStorage.getItem(that.url)) {
                  var offlineData = JSON.parse(window.localStorage.getItem(that.url))
                  that.success(offlineData)
                } else {
                  swal({
                    text: data.responseJSON.message,
                    icon: 'info',
                    button: 'Ok'
                  })
                  .then(function (data) {
                    //that.logout()
                  })
                }
              }
            })


      })
    },
  
    getMenuTypes: function () {
      var that = this
     
      var user = JSON.parse(window.localStorage.getItem('user'))
      var merchantId = user.merchant.id
    
        $.showLoading()
        $.ajax({
            beforeSend: function (xhr) {
            xhr.setRequestHeader('Authorization', window.localStorage.getItem('token'))
          },
          url: this.$config.apiUrl +'medicater/menu_types?merchant_id='+merchantId,
          type: 'get',
          success: function (data) {
            that.menu_types = data.data
            $.hideLoading()
            setTimeout(function () {
              $('.patient-block .card').matchHeight()
            }, 1000)
          },
          error: function (data) {
            console.log('data',data)
            $.hideLoading()
            if (window.localStorage.getItem(this.url)) {
              var offlineData = JSON.parse(window.localStorage.getItem(this.url))
              this.success(offlineData)
            } else {
              swal({
                text: data.responseJSON.message,
                icon: 'info',
                button: 'Ok'
              })
              .then(function (data) {
                //that.logout()
              })
            }
          }
        })
      
    },
    getBeds: function (new_ward_id="") {
      var that = this
      var ward = JSON.parse(window.localStorage.getItem('ward'))
      var ward_id = ward.ward_id
      var user = JSON.parse(window.localStorage.getItem('user'))
      var merchantId = user.merchant.id
      var has_menu_type = false
      var get_beds = false
      var occupied = true
      if(new_ward_id){
        ward_id = new_ward_id
        get_beds = true
        occupied = false
      }
      if (ward_id !== '') {
        $.showLoading()
        $.ajax({
            beforeSend: function (xhr) {
            xhr.setRequestHeader('Authorization', window.localStorage.getItem('token'))
          },
          url: this.$config.apiUrl +'medicater/beds?merchant_id='+merchantId+'&ward_id='+ward_id+"&has_menu_type="+has_menu_type+"&get_beds="+get_beds+"&occupied="+occupied,
          type: 'get',
          success: function (data) {
            if(new_ward_id){
              that.new_beds = data.data
            }else{
              that.beds = data.data
            }
            $.hideLoading()
            setTimeout(function () {
              $('.patient-block .card').matchHeight()
            }, 1000)
          },
          error: function (data) {
            console.log('data',data)
            $.hideLoading()
            if (window.localStorage.getItem(this.url)) {
              var offlineData = JSON.parse(window.localStorage.getItem(this.url))
              this.success(offlineData)
            } else {
              swal({
                text: data.responseJSON.message,
                icon: 'info',
                button: 'Ok'
              })
              .then(function (data) {
                //that.logout()
              })
            }
          }
        })
      }
    },
  }
}
</script>

<style scoped>
.allbeds-page {
  min-height: 100vh;
  background: #f0f4f8;
  padding-bottom: 40px;
}

/* ── Page header ── */
.page-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 30px 20px 20px;
  background: linear-gradient(135deg, #1a587b 0%, #0f3d5c 100%);
  position: fixed;
  top: 0; left: 0;
  width: 100%;
  z-index: 10;
  margin-top: 3.2rem;
  box-shadow: 0 8px 32px rgba(15, 61, 92, 0.35);
}

.back-btn {
  width: 36px; height: 36px;
  border-radius: 10px;
  background: rgba(255,255,255,0.15);
  border: 1px solid rgba(255,255,255,0.2);
  color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 1rem; cursor: pointer; flex-shrink: 0;
  transition: background 0.15s;
}
.back-btn:hover { background: rgba(255,255,255,0.25); }

.header-info { display: flex; flex-direction: column; gap: 3px; }
.page-title { font-size: 1.05rem; font-weight: 700; color: #fff; }
.page-subtitle { font-size: 0.73rem; color: rgba(255,255,255,0.65); font-weight: 500; display: flex; align-items: center; gap: 4px; }
.page-subtitle i { font-size: 0.65rem; }

/* ── Sections ── */
.section-pad {
 
  /* max-width: 800px; */
  margin: 0 auto;
}
.section-pad + .section-pad { padding-top: 16px; }
.beds-section { padding-top: 16px; }

/* ── Step label ── */
.step-label {
  display: flex; align-items: center; gap: 10px;
  margin-bottom: 14px; font-size: 0.875rem; font-weight: 600; color: #334155;
}
.step-badge {
  width: 24px; height: 24px; border-radius: 50%;
  background: #1a587b; color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.7rem; font-weight: 800; flex-shrink: 0;
}

/* ── Floor tabs ── */
.floor-tabs { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 16px; }
.floor-tab {
  display: flex; align-items: center; gap: 6px;
  padding: 8px 14px; border-radius: 999px;
  background: #fff; border: 1.5px solid #e8edf2;
  font-size: 0.82rem; font-weight: 600; color: #334155;
  cursor: pointer; transition: all 0.15s;
}
.floor-tab:hover { border-color: #1a587b; color: #1a587b; }
.floor-tab--active { background: #1a587b; border-color: #1a587b; color: #fff; }

/* ── Ward grid ── */
.ward-grid {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: 10px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  margin-bottom: 8px;
  padding-bottom: 4px;
  padding: .5em;
}
.ward-grid::-webkit-scrollbar { display: none; }

.ward-card {
  position: relative;
  display: flex; flex-direction: column; align-items: center; gap: 8px;
  padding: 16px 12px;
  background: #fff; border-radius: 14px;
  border: 1.5px solid #e8edf2;
  box-shadow: 0 -3px 8px rgba(18,69,100,0.18), 0 5px 14px rgba(18,69,100,0.22);
  cursor: pointer; text-align: center;
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.1s;
  font-size: 0.82rem; font-weight: 600; color: #334155;
  flex: 0 0 110px;
  min-width: 110px;
}
.ward-card:hover { border-color: #1a587b; box-shadow: 0 -4px 10px rgba(18,69,100,0.22), 0 7px 18px rgba(18,69,100,0.28); }
.ward-card:active { transform: scale(0.97); }
.ward-card--active { border-color: #1a587b; background: #f0f9ff; color: #1a587b; }
.ward-card-icon {
  width: 44px; height: 44px; border-radius: 12px;
  background: #e0f2fe; color: #1a587b;
  display: flex; align-items: center; justify-content: center;
}
.ward-card--active .ward-card-icon { background: #1a587b; color: #fff; }
.ward-card-name { line-height: 1.3; }
.ward-card-check {
  position: absolute; top: 7px; right: 7px;
  width: 20px; height: 20px; border-radius: 50%;
  background: #1a587b; color: #fff;
  display: flex; align-items: center; justify-content: center;
}

.no-wards-hint {
  font-size: 0.82rem; color: #94a3b8;
  display: flex; align-items: center; gap: 6px;
  padding: 12px 0;
}

/* ── Summary + search ── */
.ward-summary {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 12px;
}
.bed-count-badge {
  font-size: 0.72rem; font-weight: 700;
  background: #e0f2fe; color: #1a587b;
  padding: 3px 10px; border-radius: 999px;
}
.search-wrap {
  position: relative; display: flex; align-items: center;
}
.search-icon {
  position: absolute; left: 12px; color: #94a3b8; font-size: 0.85rem; pointer-events: none;
}
.search-input {
  width: 100%; padding: 10px 36px 10px 36px;
  border: 1.5px solid #e8edf2; border-radius: 10px;
  font-size: 0.875rem; color: #0f172a; background: #fff;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.search-input:focus { outline: none; border-color: #1a587b; box-shadow: 0 0 0 3px rgba(26,88,123,0.1); }
.search-clear {
  position: absolute; right: 10px;
  background: none; border: none; color: #94a3b8; cursor: pointer; padding: 4px;
  display: flex; align-items: center; font-size: 0.8rem;
}

/* ── Empty state ── */
.empty-state {
  display: flex; flex-direction: column; align-items: center;
  gap: 10px; padding: 60px 32px; text-align: center; color: #94a3b8;
}
.empty-state i { font-size: 2rem; }
.empty-state p { font-size: 0.875rem; color: #64748b; margin: 0; }
.clear-search-btn {
  margin-top: 4px; padding: 8px 18px; border-radius: 8px;
  background: #1a587b; color: #fff; border: none;
  font-size: 0.82rem; font-weight: 600; cursor: pointer;
}

/* ── Beds grid ── */
.beds-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.patient-block { display: contents; }

/* ── Bed card ── */
.bed-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #fff;
  border-radius: 14px;
  padding: 14px 14px 0;
  box-shadow: 0 4px 16px rgba(0,0,0,0.14), 0 1px 4px rgba(0,0,0,0.10);
  transition: transform 0.15s, box-shadow 0.15s;
  height: 100%;
  min-height: 150px;
}
.bed-card:active {
  transform: scale(0.98);
  box-shadow: 0 2px 8px rgba(0,0,0,0.13);
}
.bed-card--empty { opacity: 0.7; }

.bed-card-top { display: flex; align-items: center; gap: 10px; }
.patient-avatar {
  width: 38px; height: 38px; border-radius: 10px;
  background: linear-gradient(135deg, #0f4c81, #1a8a7a);
  display: flex; align-items: center; justify-content: center;
  font-size: 1rem; font-weight: 700; color: #fff; flex-shrink: 0;
}
.patient-avatar--empty { background: #e2e8f0; color: #94a3b8; }

.bed-info { flex: 1; min-width: 0; }
.bed-number { font-size: 0.7rem; font-weight: 600; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; }
.patient-name { font-size: 0.875rem; font-weight: 700; color: #0f172a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.edit-bed-btn {
  width: 28px; height: 28px; border-radius: 8px;
  background: #fff; border: 1.5px solid #e2e8f0; color: #64748b;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.9rem; cursor: pointer; flex-shrink: 0;
  margin-left: auto;
  transition: background 0.15s;
}
.edit-bed-btn:hover { background: #e0f2fe; color: #1a587b; }

.bed-card-meta { display: flex; flex-wrap: wrap; gap: 6px; }
.menu-pill, .allergy-pill {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 0.7rem; font-weight: 600; padding: 3px 8px; border-radius: 999px;
}
.menu-pill { background: rgba(26,138,122,0.1); color: #1a8a7a; }
.allergy-pill { background: rgba(220,38,38,0.08); color: #dc2626; }

.important-info {
  padding: 6px 8px;
  background: rgba(217,119,6,0.08);
  border-radius: 8px; font-size: 0.75rem; color: #d97706;
  display: flex; align-items: center; gap: 5px;
}

/* ── Meal breakdown ── */
.order-breakdown {
  display: flex;
  margin: 4px -14px 0;
  border-top: 1px solid #999a9b;
  background: #f8fafc;
  border-radius: 0 0 14px 14px;
}

.breakdown-col {
  flex: 1;
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.breakdown-divider {
  width: 1px;
  background: #999a9b;
  margin: 8px 0;
}

.breakdown-day {
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #94a3b8;
}

.breakdown-dots { display: flex; gap: 4px; flex-wrap: wrap; }

.meal-dot {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  font-size: 0.65rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
}

.dot-b { background: #0f4c81; }
.dot-l { background: #1a8a7a; }
.dot-d { background: #7c3aed; }

.meal-none { font-size: 0.8rem; color: #cbd5e1; line-height: 22px; }

.place-order-cta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 16px;
  background: #124564;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.3px;
  border-radius: 15px;
  margin-top: auto;
  margin-bottom: 1em;
  max-width: 90%;
  align-self: center;
  width: 100%;
  cursor: pointer;
  transition: opacity 0.15s;
}
.place-order-cta:hover { opacity: 0.88; }

/* ── App modals ── */
.app-modal { border-radius: 16px; overflow: hidden; border: none; }
.app-modal-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 20px;
  background: linear-gradient(135deg, #1a587b 0%, #0f3d5c 100%);
  font-size: 1rem; font-weight: 700; color: #fff;
}
.app-modal-header--danger { background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%); }
.modal-close-btn {
  width: 28px; height: 28px; border-radius: 8px;
  background: rgba(255,255,255,0.15); border: none; color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.85rem; cursor: pointer;
}
.modal-hint { font-size: 0.875rem; color: #64748b; margin: 0; }

.modal-field { margin-bottom: 14px; }
.select-label { display: block; font-size: 0.75rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 6px; }
.modal-input {
  width: 100%; padding: 10px 14px;
  border: 1.5px solid #e8edf2; border-radius: 10px;
  font-size: 0.9rem; color: #0f172a; background: #f8fafc;
  transition: border-color 0.2s;
}
.modal-input:focus { outline: none; border-color: #1a587b; }
.select-wrap { position: relative; }
.select-wrap select {
  width: 100%; padding: 10px 36px 10px 14px;
  border: 1.5px solid #e8edf2; border-radius: 10px;
  font-size: 0.9rem; color: #0f172a; background: #f8fafc;
  appearance: none; cursor: pointer;
}
.select-wrap select:focus { outline: none; border-color: #1a587b; }
.select-arrow { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); color: #94a3b8; pointer-events: none; font-size: 0.8rem; }

.app-modal-footer { display: flex; flex-wrap: wrap; gap: 8px; padding: 14px 20px 20px; }
.modal-btn {
  flex: 1; min-width: 100px; padding: 10px 16px; border-radius: 10px; border: none;
  font-size: 0.82rem; font-weight: 700; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 6px;
  transition: opacity 0.15s;
}
.modal-btn:hover { opacity: 0.88; }
.btn-discharge { background: #fee2e2; color: #dc2626; }
.btn-transfer  { background: #e0f2fe; color: #1a587b; }
.btn-edit      { background: #1a587b; color: #fff; }
.btn-order     { background: #0f3d5c; color: #fff; }
</style>
